export interface TabBarFitInput {
    /** The bar row's clientWidth. */
    available: number;
    pillWidths: number[];
    /** -1 when no pill is active. */
    activePillIndex: number;
    leading: { natural: number; floor: number; collapsible: boolean } | null;
    actionWidths: number[];
    triggerWidth: number;
    /** Between pills, and between the pills and the next segment. */
    pillGap: number;
    /** Between the leading and the action group, and between actions and the trigger. */
    gap: number;
    foldPills: boolean;
}

export interface TabBarFit {
    /** Ascending pill indices. */
    foldedPills: number[];
    leadingCollapsed: boolean;
    visibleActions: number;
    showTrigger: boolean;
}

/**
 * How many of the RIGHTMOST actions stay inline: actions[0] spills first, the last action stays inline longest.
 * `widths` are the cached px widths in display order, `available` the room left for the action group,
 * `triggerWidth` the ⋯ trigger (reserved only once at least one action spills, or always when `triggerAlwaysShown`
 * because the menu already holds something else), `gap` the px between items.
 */
export function fitActionCount(widths: number[], available: number, triggerWidth: number, gap: number, triggerAlwaysShown = false): number {
    const count = widths.length;
    if (count === 0) return 0;
    if (!triggerAlwaysShown) {
        const total = widths.reduce((sum, width) => sum + width, 0) + gap * (count - 1);
        if (total <= available) return count;
    }
    let used = triggerWidth;
    let visible = 0;
    for (let index = count - 1; index >= 0; index -= 1) {
        const next = used + gap + widths[index];
        if (next > available) break;
        used = next;
        visible += 1;
    }
    return visible;
}

const sumWithGaps = (widths: number[], gap: number) =>
    widths.length ? widths.reduce((sum, width) => sum + width, 0) + gap * (widths.length - 1) : 0;

const toFit = (foldedPills: number[], leadingCollapsed: boolean, visibleActions: number, showTrigger: boolean): TabBarFit => ({
    foldedPills,
    leadingCollapsed,
    visibleActions,
    showTrigger,
});

/** Fits pills, an optional leading element and actions into one row: actions spill, then the leading shrinks and folds, then non-active pills fold. */
export function fitTabBar(input: TabBarFitInput): TabBarFit {
    const { available, pillWidths, activePillIndex, actionWidths, triggerWidth, pillGap, gap, foldPills } = input;
    const leading = input.leading && input.leading.natural > 0 ? input.leading : null; // 0 px: the leading rendered nothing
    const leadingNatural = leading?.natural ?? 0;
    const leadingFloor = leading ? Math.min(leading.floor, leading.natural) : 0;
    const actionCount = actionWidths.length;

    const pillsWidth = (folded: number[]) =>
        sumWithGaps(
            pillWidths.filter((_, index) => !folded.includes(index)),
            pillGap,
        );
    const pillsJoint = (folded: number[]) => (pillsWidth(folded) > 0 ? pillGap : 0);
    const barWidth = (folded: number[], leadingWidth: number, visibleActions: number, showTrigger: boolean) => {
        const groupWidth = sumWithGaps([...actionWidths.slice(actionCount - visibleActions), ...(showTrigger ? [triggerWidth] : [])], gap);
        const rest = leadingWidth + (leadingWidth > 0 && groupWidth > 0 ? gap : 0) + groupWidth;
        return pillsWidth(folded) + (rest > 0 ? pillsJoint(folded) : 0) + rest;
    };

    if (barWidth([], leadingNatural, actionCount, false) <= available) return toFit([], false, actionCount, false);

    if (actionCount > 0) {
        const groupRoom = available - pillsWidth([]) - pillsJoint([]) - leadingNatural - (leading ? gap : 0);
        if (groupRoom >= triggerWidth) return toFit([], false, fitActionCount(actionWidths, groupRoom, triggerWidth, gap), true);
    }

    if (leading && barWidth([], leadingFloor, 0, actionCount > 0) <= available) return toFit([], false, 0, actionCount > 0);

    const leadingCollapsed = Boolean(leading?.collapsible);
    if (leadingCollapsed) {
        const groupRoom = available - pillsWidth([]) - pillsJoint([]);
        if (groupRoom >= triggerWidth) return toFit([], true, fitActionCount(actionWidths, groupRoom, triggerWidth, gap, true), true);
    }

    const leadingWidth = leadingCollapsed ? 0 : leadingFloor;
    const folded: number[] = [];
    if (foldPills) {
        for (let index = pillWidths.length - 1; index >= 0; index -= 1) {
            if (index === activePillIndex) continue;
            folded.unshift(index);
            if (barWidth(folded, leadingWidth, 0, true) <= available) return toFit([...folded], leadingCollapsed, 0, true);
        }
    }
    return toFit(folded, leadingCollapsed, 0, folded.length > 0 || leadingCollapsed || actionCount > 0);
}
